import { fromJS, List, Record, RecordOf } from 'immutable';

import {
  START_LOADING,
  GET_GEOFENCES_SUCCESS,
  GET_GEOFENCES_FAIL,
  ADD_GEOFENCE_SUCCESS,
  ADD_GEOFENCE_FAIL,
  UPDATE_GEOFENCE_SUCCESS,
  UPDATE_GEOFENCE_FAIL,
  DELETE_GEOFENCE_SUCCESS,
  DELETE_GEOFENCE_FAIL,
  GET_GEOFENCE_RULES_SUCCESS,
  GET_GEOFENCE_RULES_FAIL,
  UPDATE_GEOFENCE_RULE_SUCCESS,
  UPDATE_GEOFENCE_RULE_FAIL,
  CREATE_GEOFENCE_RULE_SUCCESS,
  CREATE_GEOFENCE_RULE_FAIL,
  DELETE_GEOFENCE_RULE_SUCCESS,
  DELETE_GEOFENCE_RULE_FAIL,
} from './actions';

import { GENERIC_ERROR } from 'constants/errors.constants';
import { UserGeofenceType } from 'constants/geofence';
import { GeofenceRule, UserDefinedGeofenceRecord } from 'services/resources';
import { geofenceRuleFactory } from './factories';

export const geofencesStateFactory = Record({
  geofences: List<RecordOf<UserDefinedGeofenceRecord>>(),
  geofenceRules: List<RecordOf<GeofenceRule>>(),
  loading: false,
  errors: Record<any>({})(),
});

export const geofencesInitialState = geofencesStateFactory({});

/**
 * Converts a single polygon or circle to a usable format by google maps
 */
const processSingleGeofence = (geofence) => {
  if (geofence.type === UserGeofenceType.CIRCLE) {
    return {
      ...geofence,
      center: {
        lng: geofence.center[0],
        lat: geofence.center[1],
      },
      edge: {
        lng: geofence.edge[0],
        lat: geofence.edge[1],
      },
    };
  } else {
    return {
      ...geofence,
      polygon: geofence.polygon
        .map((point) => ({
          lng: point[0],
          lat: point[1],
        }))
        .slice(0, -1),
    };
  }
};

/**
 * Converts the database list of polygon / circle to usable format by google maps.
 */
const processGeofences = (geofences) => geofences.map((geofence) => processSingleGeofence(geofence));

function geofencesServiceReducer(state = geofencesInitialState, action) {
  switch (action.type) {
    case START_LOADING:
      return state.set('loading', true);
    case GET_GEOFENCES_SUCCESS:
      return state
        .set('geofences', fromJS(processGeofences(action.payload.data.results)))
        .set('loading', false)
        .set('errors', Record({})());
    case GET_GEOFENCES_FAIL:
      return state
        .set('geofences', fromJS([]))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case ADD_GEOFENCE_SUCCESS:
      return state
        .set('geofences', state.get('geofences').push(fromJS(processSingleGeofence(action.payload.geofence))))
        .set('loading', false)
        .set('errors', Record({})());
    case UPDATE_GEOFENCE_SUCCESS: {
      const index = state.get('geofences').findIndex((geofence) => geofence.get('id') === action.payload.geofence.id);

      return state
        .set('geofences', state.get('geofences').set(index, fromJS(processSingleGeofence(action.payload.geofence))))
        .set('loading', false)
        .set('errors', Record({})());
    }
    case DELETE_GEOFENCE_SUCCESS: {
      const index = state.get('geofences').findIndex((geofence) => geofence.id === action.payload.id);

      return state
        .set('geofences', state.get('geofences').delete(index))
        .set('loading', false)
        .set('errors', Record({})());
    }
    case GET_GEOFENCE_RULES_SUCCESS:
      return state
        .set('geofenceRules', List(action.payload.data.results.map((rule) => geofenceRuleFactory(rule))))
        .set('loading', false);
    case UPDATE_GEOFENCE_RULE_SUCCESS: {
      const index = state.get('geofenceRules').findIndex((rule) => rule.id === action.payload.rule.id);

      return state
        .updateIn(['geofenceRules', index], () => geofenceRuleFactory(action.payload.rule))
        .set('loading', false)
        .set('errors', Record({})());
    }
    case CREATE_GEOFENCE_RULE_SUCCESS:
      return state
        .set('geofenceRules', state.get('geofenceRules').push(geofenceRuleFactory(action.payload.rule)))
        .set('loading', false)
        .set('errors', Record({})());
    case DELETE_GEOFENCE_RULE_SUCCESS: {
      const index = state.get('geofenceRules').findIndex((rule) => rule.id === action.payload.id);

      return state
        .set('geofenceRules', state.get('geofenceRules').delete(index))
        .set('loading', false)
        .set('errors', Record({})());
    }
    case DELETE_GEOFENCE_RULE_FAIL:
    case ADD_GEOFENCE_FAIL:
    case CREATE_GEOFENCE_RULE_FAIL:
    case UPDATE_GEOFENCE_FAIL:
    case DELETE_GEOFENCE_FAIL:
    case GET_GEOFENCE_RULES_FAIL:
    case UPDATE_GEOFENCE_RULE_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    default:
      return state;
  }
}

export default geofencesServiceReducer;
